import React, { useEffect, useMemo, useState } from "react";
import api from "../api";
import { Map, AdvancedMarker, InfoWindow, useMap } from "@vis.gl/react-google-maps";
import { GOOGLE_MAPS_MAP_ID, TAFT_CENTER } from "../components/GoogleMapsProvider";
import { Truck, ArrowsClockwise, Crosshair } from "@phosphor-icons/react";

function FitToDrivers({ drivers }) {
  const map = useMap();
  useEffect(() => {
    if (!map || !window.google?.maps || !drivers?.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    drivers.forEach((d) => bounds.extend({ lat: d.lat, lng: d.lng }));
    map.fitBounds(bounds, 80);
    const lis = window.google.maps.event.addListenerOnce(map, "idle", () => {
      if (map.getZoom() > 14) map.setZoom(14);
    });
    return () => window.google.maps.event.removeListener(lis);
  }, [map, JSON.stringify(drivers.map((d) => [d.driver_id, d.lat, d.lng]))]);
  return null;
}

function staleness(iso) {
  if (!iso) return "—";
  const diffMs = Date.now() - new Date(iso).getTime();
  const s = Math.floor(diffMs / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  return `${Math.floor(m / 60)}h ago`;
}

export default function LiveFleet() {
  const [drivers, setDrivers] = useState([]);
  const [asOf, setAsOf] = useState(null);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(false);
  const [tick, setTick] = useState(0); // for "last seen" relative time updates

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/fleet");
      setDrivers(data.drivers || []);
      setAsOf(data.as_of);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    const t = setInterval(load, 30000); // poll fleet every 30s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), 15000);
    return () => clearInterval(t);
  }, []);

  const activeDrivers = useMemo(
    () => drivers.filter((d) => d.lat != null && d.lng != null),
    [drivers]
  );

  return (
    <div className="space-y-5" data-testid="live-fleet-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.3em] text-[#5C738A]">Operations</div>
          <h1 className="font-heading text-3xl font-black tracking-tight text-[#012340] mt-1">Live Fleet</h1>
          <p className="text-sm text-[#5C738A] mt-1">
            {activeDrivers.length} driver{activeDrivers.length !== 1 ? "s" : ""} active
            {asOf && ` · refreshed ${staleness(asOf)}`}
            <span className="hidden">{tick}</span>
          </p>
        </div>
        <button onClick={load} disabled={loading} className="px-3 py-2 rounded-sm border border-[#012340]/20 text-sm font-bold text-[#012340] inline-flex items-center gap-1.5 hover:bg-[#012340]/5 disabled:opacity-50" data-testid="fleet-refresh">
          <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Driver list (left) */}
        <div className="lg:col-span-4 space-y-2 max-h-[640px] overflow-y-auto pr-1" data-testid="fleet-list">
          {activeDrivers.length === 0 && (
            <div className="bg-white border border-dashed border-[#012340]/20 rounded-sm p-6 text-center text-xs text-[#5C738A]">
              No drivers currently on route. Driver positions appear here once they log in to the driver app and start a route.
            </div>
          )}
          {activeDrivers.map((d) => {
            const isActive = selected?.driver_id === d.driver_id;
            const pct = d.total_stops
              ? Math.round(((d.completed_today || 0) + (d.issues_today || 0)) / d.total_stops * 100)
              : 0;
            return (
              <button
                key={d.driver_id}
                onClick={() => setSelected(d)}
                data-testid={`fleet-driver-${d.driver_id}`}
                className={`w-full text-left bg-white border rounded-sm p-3 transition-all hover:bg-[#FAFAFA] ${isActive ? "border-[#CFAB7A] border-l-4" : "border-[#012340]/10"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-8 h-8 rounded-sm bg-[#012340] text-[#CFAB7A] grid place-items-center flex-shrink-0">
                      <Truck size={14} weight="bold" />
                    </div>
                    <div className="min-w-0">
                      <div className="font-heading font-bold text-[#012340] truncate">{d.driver_name || "Driver"}</div>
                      <div className="text-[11px] text-[#5C738A]">
                        {d.route_number ? `Route #${d.route_number}` : "No route"}
                        {d.route_name && ` · ${d.route_name}`}
                      </div>
                    </div>
                  </div>
                  <span className={`text-[10px] uppercase tracking-[0.2em] font-bold ${d.status === "active" ? "text-[#2E7D32]" : "text-[#5C738A]"}`}>{d.status}</span>
                </div>
                {d.total_stops > 0 && (
                  <>
                    <div className="mt-2 h-1.5 bg-[#012340]/10 rounded-full overflow-hidden">
                      <div className="h-full bg-[#2E7D32]" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-1.5 text-[10px] text-[#5C738A] font-mono flex items-center justify-between">
                      <span>{d.completed_today || 0} done · {d.issues_today || 0} issues · {d.total_stops} total</span>
                      <span>{staleness(d.last_seen)}</span>
                    </div>
                  </>
                )}
                {!d.total_stops && (
                  <div className="mt-1 text-[10px] text-[#5C738A] font-mono">Last seen {staleness(d.last_seen)}</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Map (right) */}
        <div className="lg:col-span-8 bg-white border border-[#012340]/10 rounded-sm overflow-hidden">
          <div style={{ height: 640 }} data-testid="fleet-map">
            <Map
              defaultCenter={TAFT_CENTER}
              defaultZoom={11}
              mapId={GOOGLE_MAPS_MAP_ID}
              gestureHandling="greedy"
              mapTypeControl={false}
              streetViewControl={false}
              fullscreenControl={false}
              style={{ height: "100%", width: "100%" }}
            >
              {activeDrivers.map((d) => (
                <AdvancedMarker
                  key={d.driver_id}
                  position={{ lat: d.lat, lng: d.lng }}
                  onClick={() => setSelected(d)}
                >
                  <div style={{
                    position: "relative", width: 40, height: 40,
                  }}>
                    <div style={{
                      position: "absolute", inset: 0, borderRadius: "50%",
                      background: "rgba(46,125,50,0.30)",
                      animation: "rl-pulse 1.6s ease-out infinite",
                    }} />
                    <div style={{
                      position: "absolute", inset: 6, background: "#012340",
                      color: "#CFAB7A", borderRadius: "50%",
                      display: "flex", alignItems: "center", justifyContent: "center",
                      border: `2px solid ${selected?.driver_id === d.driver_id ? "#CFAB7A" : "#2E7D32"}`,
                      fontWeight: 800, fontSize: 10, fontFamily: "Chivo, sans-serif",
                    }}>{(d.route_number || "?").toString().slice(0, 3)}</div>
                    <style>{`@keyframes rl-pulse{0%{transform:scale(0.6);opacity:.8}100%{transform:scale(1.6);opacity:0}}`}</style>
                  </div>
                </AdvancedMarker>
              ))}
              {selected && selected.lat != null && (
                <InfoWindow
                  position={{ lat: selected.lat, lng: selected.lng }}
                  onCloseClick={() => setSelected(null)}
                  pixelOffset={[0, -40]}
                >
                  <div style={{ minWidth: 220 }}>
                    <div style={{ fontWeight: 700, color: "#012340" }}>{selected.driver_name}</div>
                    <div style={{ fontSize: 12, color: "#5C738A" }}>
                      {selected.route_number ? `Route #${selected.route_number}` : "No route"}
                      {selected.route_name && ` · ${selected.route_name}`}
                    </div>
                    <div style={{ fontSize: 11, marginTop: 4 }}>
                      <b>Done:</b> {selected.completed_today || 0} ·
                      <b> Issues:</b> {selected.issues_today || 0} ·
                      <b> Total:</b> {selected.total_stops || 0}
                    </div>
                    <div style={{ fontSize: 11, color: "#5C738A", marginTop: 4, fontFamily: "monospace" }}>
                      📍 {selected.lat.toFixed(5)}, {selected.lng.toFixed(5)}
                    </div>
                    <div style={{ fontSize: 11, color: "#5C738A", marginTop: 2 }}>
                      Updated {staleness(selected.last_seen)}
                    </div>
                  </div>
                </InfoWindow>
              )}
              <FitToDrivers drivers={activeDrivers} />
            </Map>
          </div>
        </div>
      </div>

      <p className="text-[10px] text-[#5C738A]">
        Drivers ping their GPS to the server every 60 seconds while on an active route. Markers go offline 10 minutes after the last ping.
      </p>
    </div>
  );
}
